import { useEffect, useState, type ReactNode } from 'react'
import {
  browserLocalPersistence,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { isNativeApp } from '../lib/native'
import { auth } from '../lib/firebase'
import { PlayerContext, type PlayerContextValue } from './playerContext'
import {
  ensurePlayerProfile,
  savePlayerProfile,
  subscribePlayerProfile,
  type PlayerProfile,
} from './playerProfile'
const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({ prompt: 'select_account' })

function friendlyError(error: unknown) {
  if (!(error instanceof Error)) return 'Player login could not be completed.'
  if (error.message.includes('popup-closed-by-user')) return ''
  if (error.message.includes('popup-blocked')) return 'Your browser blocked the sign-in window. Please allow popups and try again.'
  if (error.message.includes('unauthorized-domain')) return 'This website domain is not authorized for player login yet.'
  if (error.message.includes('operation-not-allowed')) return 'Guest login is not enabled for this Firebase project.'
  if (error.message.includes('network-request-failed')) return 'Player login could not reach Firebase. Check your connection and try again.'
  return 'Player login could not be completed. Please try again.'
}

async function nativeGoogleSignIn(currentUser: User | null) {
  const currentAuth = auth
  if (!currentAuth) throw new Error('Player login is not configured.')

  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication')
  const result = await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true })
  const idToken = result.credential?.idToken
  if (!idToken) throw new Error('Google did not return a sign-in credential.')

  const credential = GoogleAuthProvider.credential(idToken, result.credential?.accessToken)
  return currentUser?.isAnonymous
    ? linkWithCredential(currentUser, credential)
    : signInWithCredential(currentAuth, credential)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<PlayerProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(Boolean(auth))
  const [loading, setLoading] = useState(Boolean(auth))
  const [error, setError] = useState('')

  useEffect(() => {
    if (!auth) return

    void setPersistence(auth, browserLocalPersistence)
    let stopProfile: () => void = () => undefined
    const stopAuth = onAuthStateChanged(auth, (nextUser) => {
      stopProfile()
      setUser(nextUser)
      setLoading(false)
      setProfile(null)
      setProfileLoading(Boolean(nextUser))
      if (nextUser) {
        stopProfile = subscribePlayerProfile(nextUser, (nextProfile) => {
          setProfile(nextProfile)
          setProfileLoading(false)
        }, (message) => {
          setError(message)
          setProfileLoading(false)
        })
        void ensurePlayerProfile(nextUser).catch(() => {
          setError('Your player profile could not be prepared.')
          setProfileLoading(false)
        })
      }
    })
    return () => {
      stopProfile()
      stopAuth()
    }
  }, [])

  const runSignIn = async (operation: () => Promise<{ user: User }>) => {
    setError('')
    try {
      // The auth-state observer owns profile creation. Calling it here as well
      // races two first-time writes for anonymous users and can make the second
      // write fail the immutable createdAt rule.
      await operation()
    } catch (signInError) {
      const message = friendlyError(signInError)
      setError(message)
      throw new Error(message || 'Player login was cancelled.', { cause: signInError })
    }
  }

  const value: PlayerContextValue = {
    user,
    profile,
    profileLoading,
    loading,
    available: Boolean(auth),
    error,
    signInWithGoogle: async () => {
      const currentAuth = auth
      if (!currentAuth) return
      const currentUser = currentAuth.currentUser
      await runSignIn(() => isNativeApp
        ? nativeGoogleSignIn(currentUser)
        : currentUser?.isAnonymous
          ? linkWithPopup(currentUser, googleProvider)
          : signInWithPopup(currentAuth, googleProvider))
    },
    continueAsGuest: async () => {
      const currentAuth = auth
      if (currentAuth) await runSignIn(() => signInAnonymously(currentAuth))
    },
    signOutPlayer: async () => {
      if (!auth) return
      if (isNativeApp) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication')
        await FirebaseAuthentication.signOut().catch(() => undefined)
      }
      await signOut(auth)
    },
    saveProfile: async (nextProfile) => {
      const currentUser = auth?.currentUser
      if (!currentUser) throw new Error('Sign in before editing your profile.')
      await savePlayerProfile(currentUser, nextProfile)
    },
    clearError: () => setError(''),
  }

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
}
